import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { hapticTick } from '../../_utils/haptics';
import DiaryDatePicker from '../Diary/DiaryDatePicker';
import { STATE_LABEL } from './data/codes';

// Small pieces shared by the codes page, the editor and the share sheet.

const svg = (children, size = 18, extra = {}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...extra}>{children}</svg>
);

export const Icon = {
  Ticket: ({ size = 18 }) => svg(<><path d="M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4z" /><path d="M9.5 14.5l5-5" /><circle cx="9.5" cy="9.5" r=".6" fill="currentColor" /><circle cx="14.5" cy="14.5" r=".6" fill="currentColor" /></>, size),
  Share: ({ size = 16 }) => svg(<><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></>, size),
  More: ({ size = 18 }) => svg(<><circle cx="5" cy="12" r="1.3" fill="currentColor" /><circle cx="12" cy="12" r="1.3" fill="currentColor" /><circle cx="19" cy="12" r="1.3" fill="currentColor" /></>, size),
  Info: ({ size = 16 }) => svg(<><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>, size),
  Alert: ({ size = 16 }) => svg(<><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>, size),
  Plus: ({ size = 16 }) => svg(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>, size),
  Tick: ({ size = 11 }) => svg(<polyline points="20 6 9 17 4 12" />, size, { stroke: '#fff', strokeWidth: 3.5 }),
  Copy: ({ size = 16 }) => svg(<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>, size),
  Post: ({ size = 18 }) => svg(<><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r=".6" fill="currentColor" /></>, size),
  Mail: ({ size = 18 }) => svg(<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>, size),
  Text: ({ size = 18 }) => svg(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />, size),
  Download: ({ size = 16 }) => svg(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>, size),
};

export function Pill({ state }) {
  return <span className={`dc-pill dc-pill--${state}`}>{STATE_LABEL[state] || state}</span>;
}

export function Switch({ on, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      disabled={disabled}
      className={`dc-switch${on ? ' is-on' : ''}`}
      onClick={() => { hapticTick(); onChange(!on); }}
    />
  );
}

export function Seg({ options, value, onChange, label }) {
  return (
    <div className="dc-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? 'is-on' : ''} onClick={() => { if (value !== o.value) { hapticTick(); onChange(o.value); } }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Chips({ options, value, onChange, label, multiple = false, day = false }) {
  const isOn = (v) => (multiple ? (value || []).includes(v) : value === v);
  const pick = (v) => {
    hapticTick();
    if (!multiple) { onChange(v); return; }
    const cur = value || [];
    onChange(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };
  return (
    <div className="dc-chips" role={multiple ? 'group' : 'radiogroup'} aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role={multiple ? 'checkbox' : 'radio'}
          aria-checked={isOn(o.value)}
          aria-label={o.aria || undefined}
          className={`dc-chip${day ? ' dc-chip--day' : ''}${isOn(o.value) ? ' is-on' : ''}`}
          onClick={() => pick(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Stepper({ value, onChange, min = 1, max = 99, said, label }) {
  return (
    <div className="dc-stepper" role="group" aria-label={label}>
      <button type="button" aria-label="Fewer" disabled={value <= min} onClick={() => { hapticTick(); onChange(Math.max(min, value - 1)); }}>−</button>
      <span aria-live="polite">{said ? said(value) : value}</span>
      <button type="button" aria-label="More" disabled={value >= max} onClick={() => { hapticTick(); onChange(Math.min(max, value + 1)); }}>+</button>
    </div>
  );
}

export function Advice({ children }) {
  return (
    <div className="dc-advice" role="note">
      <Icon.Alert size={15} />
      <span>{children}</span>
    </div>
  );
}

// The ⋯ menu on a card. Closes on outside tap or Escape.
export function MoreMenu({ items, label = 'More' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', off);
    document.addEventListener('touchstart', off);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', off);
      document.removeEventListener('touchstart', off);
      document.removeEventListener('keydown', key);
    };
  }, [open]);
  return (
    <div className="dc-menu-wrap" ref={ref}>
      <button type="button" className="dc-btn dc-btn--icon" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={(e) => { e.stopPropagation(); hapticTick(); setOpen((o) => !o); }}>
        <Icon.More />
      </button>
      {open && (
        <div className="dc-menu" role="menu" onClick={(e) => e.stopPropagation()}>
          {items.filter(Boolean).map((it) => (
            <button key={it.label} type="button" role="menuitem" className={it.danger ? 'is-danger' : ''} onClick={() => { setOpen(false); it.onClick(); }}>{it.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// A confirm over everything (panels included), with an optional reason box.
// Cancel on the left, the action on the right.
export function ConfirmSheet({ title, text, confirm, danger = false, reason = null, onCancel, onConfirm, busy = false }) {
  const [note, setNote] = useState('');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const needs = reason && reason.required;
  return ReactDOM.createPortal(
    <div className="sp-confirm-overlay" style={{ zIndex: 100020 }} onClick={onCancel}>
      <div className="sp-confirm-card dc-page" role="alertdialog" aria-modal="true" aria-labelledby="dc-confirm-title" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'left' }}>
        <h3 className="sp-confirm-title" id="dc-confirm-title">{title}</h3>
        {text && <p className="sp-confirm-text">{text}</p>}
        {reason && (
          <div className="dc-reason" style={{ marginBottom: 16 }}>
            <label className="dc-label" htmlFor="dc-confirm-reason">{reason.label}</label>
            <textarea id="dc-confirm-reason" value={note} placeholder={reason.placeholder || ''} onChange={(e) => { const v = e.target.value; setNote(v); }} />
          </div>
        )}
        <div className="sp-confirm-actions">
          <button type="button" className="sp-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="sp-btn-primary"
            style={danger ? { background: '#b3261e' } : undefined}
            disabled={busy || (needs && !note.trim())}
            onClick={() => onConfirm(note.trim())}
          >
            {confirm}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// A date, picked on the diary's own calendar (as Social's day field is).
// `min` refuses earlier days with a reason; `onClear` adds a way to empty an
// optional date.
export function DateField({ id, value, onChange, min, minSaid, placeholder = 'Choose a date', disabled = false, bad = false, onClear }) {
  const [open, setOpen] = useState(false);
  const [refused, setRefused] = useState('');
  const anchor = useRef(null);
  return (
    <>
      <div className="dc-date-row">
        <button
          id={id}
          type="button"
          ref={anchor}
          disabled={disabled}
          aria-haspopup="dialog"
          className={`dc-input dc-date${value ? '' : ' is-empty'}${value && onClear && !disabled ? ' has-clear' : ''}${bad || refused ? ' is-bad' : ''}`}
          onClick={() => { hapticTick(); setOpen(true); }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
          <span>{value ? moment(value, 'YYYY-MM-DD').format('ddd D MMM YYYY') : placeholder}</span>
        </button>
        {value && onClear && !disabled && (
          <button type="button" className="dc-date-clear" aria-label="Clear this date" onClick={() => { hapticTick(); setRefused(''); onClear(); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        )}
      </div>
      {refused && <p className="dc-err" role="alert">{refused}</p>}
      <DiaryDatePicker
        open={open}
        selected={value ? moment(value, 'YYYY-MM-DD').toDate() : (min ? moment(min, 'YYYY-MM-DD').toDate() : new Date())}
        anchorEl={anchor.current}
        onChange={(d) => {
          setOpen(false);
          const ymd = moment(d).format('YYYY-MM-DD');
          if (min && ymd < min) { setRefused(`Choose ${minSaid || `${moment(min, 'YYYY-MM-DD').format('ddd D MMM')} or later`}.`); return; }
          setRefused('');
          onChange(ymd);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
